import type { IntakeFieldDef } from "./intakeFields.js";

/**
 * Renders ONE typed field from an `IntakeFieldDef`. This is the entire "industry-dependent
 * UI" mechanism: the same component renders a vet's "species" dropdown, an auto shop's VIN
 * field, or a dental clinic's "new patient" toggle, driven purely by the field's `type` --
 * no industry branching lives in this file. See docs/architecture.md section 15.
 */
export function IntakeFieldInput({
  field,
  value,
  onChange,
}: {
  field: IntakeFieldDef;
  value: unknown;
  onChange: (next: unknown) => void;
}) {
  const label = (
    <span>
      {field.label}
      {field.required ? " *" : ""}
    </span>
  );

  switch (field.type) {
    case "long_text":
      return (
        <label style={{ fontSize: 13, display: "block" }}>
          {label}
          <textarea
            required={field.required}
            value={(value as string) ?? ""}
            onChange={(e) => onChange(e.target.value)}
            rows={2}
            style={{ ...fieldStyle, width: "100%", resize: "vertical" }}
          />
        </label>
      );

    case "single_select":
      return (
        <label style={{ fontSize: 13, display: "block" }}>
          {label}
          <select
            required={field.required}
            value={(value as string) ?? ""}
            onChange={(e) => onChange(e.target.value)}
            style={fieldStyle}
          >
            <option value="">(select)</option>
            {(field.options ?? []).map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </label>
      );

    case "multi_select": {
      const selected = Array.isArray(value) ? (value as string[]) : [];
      return (
        <fieldset style={{ fontSize: 13, border: "none", padding: 0, margin: 0 }}>
          <legend style={{ padding: 0, fontSize: 13 }}>{label}</legend>
          {(field.options ?? []).map((opt) => (
            <label key={opt} style={{ display: "block", fontWeight: "normal" }}>
              <input
                type="checkbox"
                checked={selected.includes(opt)}
                onChange={(e) =>
                  onChange(e.target.checked ? [...selected, opt] : selected.filter((o) => o !== opt))
                }
              />{" "}
              {opt}
            </label>
          ))}
        </fieldset>
      );
    }

    case "boolean":
      return (
        <label style={{ fontSize: 13, display: "block" }}>
          <input type="checkbox" checked={Boolean(value)} onChange={(e) => onChange(e.target.checked)} /> {field.label}
        </label>
      );

    case "number":
      return (
        <label style={{ fontSize: 13, display: "block" }}>
          {label}
          <input
            type="number"
            required={field.required}
            value={(value as string) ?? ""}
            onChange={(e) => onChange(e.target.value)}
            style={fieldStyle}
          />
        </label>
      );

    case "date":
      return (
        <label style={{ fontSize: 13, display: "block" }}>
          {label}
          <input
            type="date"
            required={field.required}
            value={(value as string) ?? ""}
            onChange={(e) => onChange(e.target.value)}
            style={fieldStyle}
          />
        </label>
      );

    case "phone":
      return (
        <label style={{ fontSize: 13, display: "block" }}>
          {label}
          <input
            type="tel"
            required={field.required}
            value={(value as string) ?? ""}
            onChange={(e) => onChange(e.target.value)}
            style={fieldStyle}
          />
        </label>
      );

    case "email":
      return (
        <label style={{ fontSize: 13, display: "block" }}>
          {label}
          <input
            type="email"
            required={field.required}
            value={(value as string) ?? ""}
            onChange={(e) => onChange(e.target.value)}
            style={fieldStyle}
          />
        </label>
      );

    case "address":
    case "identifier":
    case "short_text":
    default:
      return (
        <label style={{ fontSize: 13, display: "block" }}>
          {label}
          <input
            type="text"
            required={field.required}
            value={(value as string) ?? ""}
            onChange={(e) => onChange(e.target.value)}
            style={fieldStyle}
          />
        </label>
      );
  }
}

const fieldStyle: React.CSSProperties = { display: "block", padding: 6, marginTop: 4, width: "100%", boxSizing: "border-box" };
